import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, IconButton,
  Tooltip, CircularProgress, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Select, MenuItem, FormControl, InputLabel, Button, Divider, Stack,
  Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import apiService from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────

interface SignalVector {
  id: number;
  symbol: string;
  direction: number;
  magnitude: number;
  composite_score: number;
  confidence: number;
  timeframe: string;
  category: string;
  reasoning: string;
  model_used: string;
  generated_at: string;
}

interface NewsArticle {
  id: number;
  source: string;
  title: string;
  summary?: string;
  url: string;
  published_at: string;
  category?: string;
  signals: SignalVector[];
}

interface NotRelevantArticle {
  id: number;
  source: string;
  title: string;
  summary?: string;
  url: string;
  published_at: string;
  analyzed_at: string;
  category?: string;
}

interface ArticleRef {
  id: number;
  title: string;
  source: string;
  published_at: string;
  url: string;
  is_fresh: boolean;
  is_post_mortem: boolean;
}

interface PriceData {
  symbol: string;
  price: number;
  prev_close: number;
  change_pct: number;
  updated_at: string;
}

interface StockAggregation {
  symbol: string;
  company_name: string | null;
  sector: string | null;
  composite_score: number;
  claude_composite: number | null;
  qwen_composite: number | null;
  deepseek_composite: number | null;
  avg_direction: number;
  avg_magnitude: number;
  avg_confidence: number;
  signal_count: number;
  article_count: number;
  forward_article_count: number;
  post_mortem_count: number;
  is_actionable: boolean;
  dominant_category: string;
  dominant_timeframe: string;
  articles: ArticleRef[];
}

interface HeatmapItem {
  symbol: string;
  composite_score: number;
  count: number;
}

interface AccuracyRow {
  category: string;
  model: string;
  total: number;
  correct: number;
  accuracy: number;
  base_rate: number;   // the same days' named stocks, whatever the models said
  bullish: number;
  bearish: number;
}

interface SchedulerStatus {
  pending_articles: number;
  analyzed_articles: number;
  relevant_articles: number;
  not_relevant_articles: number;
  total_signals: number;
  claude_signals: number;
  qwen_signals: number;
  deepseek_signals: number;
  claude_available: boolean;
  qwen_available: boolean;
  deepseek_available: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────

// High-contrast green / red palette — dark text on pale background for legibility
const BULL_COLOR = '#1B5E20';   // dark green (high contrast on light bg)
const BEAR_COLOR = '#B71C1C';   // dark red   (high contrast on light bg)
const BULL_BG    = '#E8F5E9';   // pale green
const BEAR_BG    = '#FFEBEE';   // pale red
const BULL_BORDER = '#43A047';  // medium green
const BEAR_BORDER = '#E53935';  // medium red


const DirectionIcon: React.FC<{ score: number; size?: 'small' | 'medium' }> = ({ score, size = 'small' }) => {
  if (score > 0.05) return <TrendingUpIcon fontSize={size} sx={{ color: BULL_BORDER }} />;
  if (score < -0.05) return <TrendingDownIcon fontSize={size} sx={{ color: BEAR_BORDER }} />;
  return <TrendingFlatIcon fontSize={size} color="disabled" />;
};

const ScoreBar: React.FC<{ score: number }> = ({ score }) => {
  const isBull = score >= 0;
  const pct = Math.round(Math.abs(score) * 100);
  const barColor = isBull ? BULL_BORDER : BEAR_BORDER;
  const textColor = isBull ? BULL_COLOR : BEAR_COLOR;
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Box sx={{ width: 60, height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: barColor, borderRadius: 3 }} />
      </Box>
      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: textColor, fontWeight: 700 }}>
        {isBull ? '+' : ''}{score.toFixed(2)}
      </Typography>
    </Box>
  );
};

const modelLabel = (model: string) => {
  if (model.includes('claude')) return 'Claude';
  if (model.includes('deepseek')) return 'DeepSeek';
  if (model.startsWith('qwen3.5:4b')) return 'Qwen 3.5 4B';
  if (model.startsWith('qwen3.5-9b-iq3')) return 'Qwen 3.5 9B (3-bit)';
  if (model.startsWith('qwen3:4b')) return 'Qwen 3 4B';
  if (model === 'qwen3.5' || model === 'qwen3.5:latest') return 'Qwen 3.5 9B';
  if (model.includes('qwen')) return 'Qwen';
  return model;
};

const modelColor = (model: string): 'primary' | 'secondary' | 'default' => {
  if (model.includes('claude')) return 'primary';
  if (model.includes('deepseek')) return 'secondary';
  if (model.includes('qwen')) return 'secondary';
  return 'default';
};

const modelIcon = (model: string) => {
  if (model.includes('claude')) return '🤖';
  if (model.includes('deepseek')) return '🧠';
  return '🦾';
};

const sourceColor = (source: string): string => {
  const colors: Record<string, string> = {
    // Wire services
    reuters:        '#FF6B00',
    bloomberg:      '#6B48FF',
    google_finance: '#1A73E8',
    google_earnings:'#0F9D58',
    // Institutional
    ibd:            '#8B0000',
    the_street:     '#E65100',
    ft:             '#FFC107',
    nasdaq:         '#1565C0',
    // Press releases
    globenewswire:  '#2E7D32',
    prnewswire:     '#00695C',
    // Retail / commentary
    wsj:            '#333333',
    cnbc:           '#4A90E2',
    marketwatch:    '#1B5E20',
    yahoo_finance:  '#6001D2',
    seeking_alpha:  '#F5A623',
    benzinga:       '#00BCD4',
    motley_fool:    '#C62828',
    // Tech
    techcrunch:     '#0A8F08',
    the_verge:      '#FF3333',
  };
  return colors[source] || '#757575';
};

const timeAgo = (dateStr: string) => {
  // Backend returns naive UTC strings without 'Z'. Browsers treat those as local
  // time, inflating the apparent age by the local UTC offset.  Append 'Z' so the
  // browser always parses as UTC.
  const utcStr = /Z$|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  const diff = Date.now() - new Date(utcStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ── Sub-components ────────────────────────────────────────────────────────

const cleanSymbol = (sym: string) =>
  sym.endsWith('_q') || sym.endsWith('_d') ? sym.slice(0, -2) : sym;

interface StockMeta { name: string | null; sector: string | null; }

const SignalChip: React.FC<{ signal: SignalVector; meta?: StockMeta }> = ({ signal, meta }) => {
  const score = signal.composite_score;
  const displaySym = cleanSymbol(signal.symbol);
  const isBull = score > 0.05;
  const isBear = score < -0.05;
  const bg = isBull ? BULL_BG : isBear ? BEAR_BG : '#F5F5F5';
  const textColor = isBull ? BULL_COLOR : isBear ? BEAR_COLOR : '#616161';
  const borderColor = isBull ? BULL_BORDER : isBear ? BEAR_BORDER : '#9E9E9E';
  const border = `1.5px solid ${borderColor}`;

  // Magnitude bar width (0–100%)
  const magPct = Math.round(signal.magnitude * 100);

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 280 }}>
          {meta?.name && (
            <Typography variant="body2" fontWeight={700} mb={0.2}>
              {meta.name}{meta.sector ? ` · ${meta.sector}` : ''}
            </Typography>
          )}
          <Typography variant="body2" fontWeight={meta?.name ? 400 : 700}>{displaySym} — {modelLabel(signal.model_used)}</Typography>
          <Typography variant="caption" display="block" mt={0.3}>
            Composite: {score > 0 ? '+' : ''}{score.toFixed(2)} · Direction: {signal.direction > 0 ? '+' : ''}{signal.direction.toFixed(2)}
          </Typography>
          <Typography variant="caption" display="block">
            Magnitude: {signal.magnitude.toFixed(2)} · Confidence: {Math.round(signal.confidence * 100)}% · {signal.timeframe}
          </Typography>
          <Typography variant="caption" display="block" mt={0.5} sx={{ fontStyle: 'italic' }}>{signal.reasoning}</Typography>
        </Box>
      }
      arrow
    >
      <Box sx={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
        bgcolor: bg, border, borderRadius: '8px',
        px: 1, py: 0.5, minWidth: 64, cursor: 'default',
      }}>
        {/* Row 1: icon + symbol + model */}
        <Box display="flex" alignItems="center" gap={0.4}>
          <DirectionIcon score={score} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: textColor }}>{displaySym}</Typography>
          <Typography sx={{ fontSize: '0.65rem', lineHeight: 1 }}>{modelIcon(signal.model_used)}</Typography>
        </Box>
        {/* Row 2: composite score */}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: textColor, lineHeight: 1.2 }}>
          {score > 0 ? '+' : ''}{score.toFixed(2)}
        </Typography>
        {/* Row 3: magnitude bar */}
        <Box sx={{ width: '100%', height: 3, bgcolor: `${borderColor}30`, borderRadius: 2, overflow: 'hidden', mt: 0.3 }}>
          <Box sx={{ width: `${magPct}%`, height: '100%', bgcolor: borderColor, borderRadius: 2 }} />
        </Box>
        <Typography sx={{ fontSize: '0.58rem', color: borderColor, lineHeight: 1.2 }}>
          mag {signal.magnitude.toFixed(1)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const ArticleCard: React.FC<{ article: NewsArticle; stockMeta?: Record<string, StockMeta> }> = ({ article, stockMeta = {} }) => {
  // Group by clean symbol, then show all per-model chips for each symbol.
  // Sort symbols by strongest composite score across any model.
  const bySymbol = new Map<string, SignalVector[]>();
  for (const sig of article.signals) {
    const sym = cleanSymbol(sig.symbol);
    if (!bySymbol.has(sym)) bySymbol.set(sym, []);
    bySymbol.get(sym)!.push(sig);
  }
  const sortedSymbols = [...bySymbol.entries()]
    .sort((a, b) => Math.max(...b[1].map(s => Math.abs(s.composite_score))) - Math.max(...a[1].map(s => Math.abs(s.composite_score))))
    .slice(0, 10);  // max 10 distinct stocks shown

  return (
    <Card variant="outlined" sx={{ mb: 2, borderWidth: 1.5 }}>
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.75} flexWrap="wrap">
              <Chip label={article.source.replace('_', ' ')} size="small"
                sx={{ bgcolor: sourceColor(article.source), color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
              {article.category && (
                <Chip label={article.category} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
              )}
              <Typography variant="body2" color="text.secondary" fontWeight={500}>{timeAgo(article.published_at)}</Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.75, lineHeight: 1.45, fontSize: '1rem' }}>
              {article.title}
            </Typography>
            {article.summary && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}>
                {article.summary.slice(0, 300)}{article.summary.length > 300 ? '…' : ''}
              </Typography>
            )}
          </Box>
          <Tooltip title="Open article">
            <IconButton href={article.url} target="_blank" rel="noopener" sx={{ mt: 0.5, flexShrink: 0 }}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {sortedSymbols.length > 0 && (
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box display="flex" gap={1.5} flexWrap="wrap">
              {sortedSymbols.map(([sym, sigs]) => (
                <Box key={sym} display="flex" gap={0.5} flexWrap="nowrap">
                  {sigs
                    .sort((a, b) => Math.abs(b.composite_score) - Math.abs(a.composite_score))
                    .map(sig => <SignalChip key={sig.id} signal={sig} meta={stockMeta[sym]} />)}
                </Box>
              ))}
              {bySymbol.size > 10 && (
                <Chip label={`+${bySymbol.size - 10} more stocks`} variant="outlined" sx={{ alignSelf: 'center' }} />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const NotRelevantCard: React.FC<{ article: NotRelevantArticle }> = ({ article }) => (
  <Card variant="outlined" sx={{ mb: 1, opacity: 0.75 }}>
    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.3} flexWrap="wrap">
            <Chip label={article.source.replace('_', ' ')} size="small"
              sx={{ bgcolor: sourceColor(article.source), color: '#fff', fontWeight: 600, fontSize: '0.6rem', height: 18 }} />
            {article.category && <Chip label={article.category} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />}
            <Typography variant="caption" color="text.disabled">{timeAgo(article.published_at)}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>{article.title}</Typography>
        </Box>
        <Tooltip title="Open article">
          <IconButton size="small" href={article.url} target="_blank" rel="noopener"><OpenInNewIcon fontSize="inherit" /></IconButton>
        </Tooltip>
      </Box>
    </CardContent>
  </Card>
);

// ── Stock Aggregation Card ────────────────────────────────────────────────

const AggregationCard: React.FC<{ item: StockAggregation; price?: PriceData }> = ({ item, price }) => {
  const [expanded, setExpanded] = useState(false);
  const score = item.composite_score;
  const isBull = score > 0.05;
  const isBear = score < -0.05;

  const qwenOk = item.qwen_composite !== null;
  const deepseekOk = item.deepseek_composite !== null;

  // Consensus: both local models agree on direction
  const composites = [
    qwenOk ? item.qwen_composite! : null,
    deepseekOk ? item.deepseek_composite! : null,
  ].filter(v => v !== null) as number[];
  const bullish = composites.filter(v => v > 0).length;
  const bearish = composites.filter(v => v < 0).length;
  const agree = composites.length >= 2
    ? (bullish === composites.length || bearish === composites.length ? true : false)
    : null;

  const chipColor = (v: number) => {
    if (v > 0.05) return { bgcolor: BULL_BG, color: BULL_COLOR, border: `1px solid ${BULL_BORDER}` };
    if (v < -0.05) return { bgcolor: BEAR_BG, color: BEAR_COLOR, border: `1px solid ${BEAR_BORDER}` };
    return { bgcolor: 'grey.100', color: '#616161', border: '1px solid #9E9E9E' };
  };

  return (
    <Card variant="outlined" sx={{
      mb: 1.5,
      borderColor: isBull ? BULL_BORDER : isBear ? BEAR_BORDER : 'divider',
      borderWidth: 1.5,
    }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        {/* Header row: symbol + price + score + model chips */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
          {/* Ticker with company name/sector tooltip */}
          <Tooltip
            title={item.company_name
              ? `${item.company_name}${item.sector ? ` · ${item.sector}` : ''}`
              : item.symbol}
            placement="top"
            arrow
          >
            <Typography variant="h5" fontWeight={800} sx={{ minWidth: 64, cursor: 'default' }}>
              {item.symbol}
            </Typography>
          </Tooltip>

          {/* Live price + change */}
          {price && (
            <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ lineHeight: 1 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
                ${price.price.toFixed(2)}
              </Typography>
              <Typography sx={{
                fontSize: '0.72rem', fontWeight: 600, lineHeight: 1,
                color: price.change_pct >= 0 ? BULL_COLOR : BEAR_COLOR,
              }}>
                {price.change_pct >= 0 ? '+' : ''}{price.change_pct.toFixed(2)}%
              </Typography>
            </Box>
          )}

          <ScoreBar score={score} />

          {/* Qwen chip — always shown */}
          <Tooltip title={qwenOk ? `Qwen composite: ${item.qwen_composite!.toFixed(2)}` : 'Qwen analysis pending'}>
            <Chip size="small"
              label={qwenOk
                ? `🦾 ${item.qwen_composite! > 0 ? '+' : ''}${item.qwen_composite!.toFixed(2)}`
                : '🦾 Pending'}
              sx={{
                fontSize: '0.75rem', height: 24,
                ...(qwenOk ? chipColor(item.qwen_composite!) : { bgcolor: 'grey.100', color: '#9E9E9E', border: '1px solid #E0E0E0' }),
              }}
            />
          </Tooltip>

          {/* DeepSeek chip — always shown, "Pending" when null */}
          <Tooltip title={deepseekOk ? `DeepSeek-R1 composite: ${item.deepseek_composite!.toFixed(2)}` : 'DeepSeek analysis still processing (slow model)'}>
            <Chip size="small"
              label={deepseekOk
                ? `🧠 ${item.deepseek_composite! > 0 ? '+' : ''}${item.deepseek_composite!.toFixed(2)}`
                : '🧠 Pending'}
              sx={{
                fontSize: '0.75rem', height: 24,
                ...(deepseekOk ? chipColor(item.deepseek_composite!) : { bgcolor: 'grey.100', color: '#9E9E9E', border: '1px dashed #BDBDBD' }),
              }}
            />
          </Tooltip>

          {agree !== null && (
            <Tooltip title={agree ? 'Models agree on direction' : 'Models disagree on direction'}>
              <Chip size="small" label={agree ? '✓ Consensus' : '⚡ Split'}
                sx={{
                  fontSize: '0.72rem', height: 22,
                  bgcolor: agree ? '#E8F5E9' : '#FFF8E1',
                  color: agree ? '#2E7D32' : '#F57F17',
                  border: agree ? '1px solid #A5D6A7' : '1px solid #FFE082',
                }}
              />
            </Tooltip>
          )}

          <Box flex={1} />
          <IconButton size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Metrics row: articles · signals · category · timeframe · confidence */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Tooltip title={item.post_mortem_count > 0
            ? `${item.forward_article_count} forward-looking, ${item.post_mortem_count} describe past price moves`
            : `${item.article_count} forward-looking articles`}>
            <Typography variant="body2" color={item.post_mortem_count > 0 ? 'warning.dark' : 'text.secondary'}>
              {item.post_mortem_count > 0 && '⚠ '}
              {item.forward_article_count} forward · {item.article_count} total · {item.signal_count} signal{item.signal_count !== 1 ? 's' : ''}
            </Typography>
          </Tooltip>
          <Chip size="small" label={item.dominant_category} variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
          <Chip size="small" label={item.dominant_timeframe} variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
          <Typography variant="body2" color="text.secondary">conf {Math.round(item.avg_confidence * 100)}%</Typography>
        </Box>

        {/* Expanded: contributing articles */}
        <Collapse in={expanded}>
          <Box mt={1.5} pl={1.5} borderLeft={2} borderColor="divider">
            {item.articles.map(a => (
              <Box key={a.id} display="flex" alignItems="flex-start" gap={0.75} mt={1}
                sx={{ opacity: a.is_post_mortem ? 0.55 : 1 }}>
                <Chip label={a.source.replace('_', ' ')} size="small"
                  sx={{ bgcolor: sourceColor(a.source), color: '#fff', fontSize: '0.65rem', height: 20, flexShrink: 0 }} />
                {a.is_fresh && (
                  <Tooltip title="Fresh article (< 12h) — market still absorbing">
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700, flexShrink: 0 }}>🆕</Typography>
                  </Tooltip>
                )}
                {a.is_post_mortem && (
                  <Tooltip title="Describes a quantified price move that already occurred">
                    <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700, flexShrink: 0 }}>⚠</Typography>
                  </Tooltip>
                )}
                <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.3, fontSize: '0.8rem' }}>
                  {a.title.slice(0, 120)}{a.title.length > 120 ? '…' : ''}
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: '0.75rem' }}>
                  {timeAgo(a.published_at)}
                </Typography>
                <IconButton size="small" href={a.url} target="_blank" rel="noopener" sx={{ p: 0.2, flexShrink: 0 }}>
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ── Heatmap ───────────────────────────────────────────────────────────────

// Opaque shades, weakest → strongest, each with a text colour that reads on it in
// either theme: dark text on the pale steps, white on the saturated ones.
const HEAT_BULL = [
  { bg: '#E8F5E9', fg: '#1B5E20' }, { bg: '#C8E6C9', fg: '#1B5E20' }, { bg: '#81C784', fg: '#0B3D0F' },
  { bg: '#388E3C', fg: '#FFFFFF' }, { bg: '#1B5E20', fg: '#FFFFFF' },
];
const HEAT_BEAR = [
  { bg: '#FFEBEE', fg: '#B71C1C' }, { bg: '#FFCDD2', fg: '#B71C1C' }, { bg: '#E57373', fg: '#4A0A0A' },
  { bg: '#D32F2F', fg: '#FFFFFF' }, { bg: '#8E1B1B', fg: '#FFFFFF' },
];
const HEAT_FLAT = { bg: '#EEEEEE', fg: '#424242' };

const heatShade = (pct: number) => {
  if (pct === 0) return HEAT_FLAT;
  const scale = pct > 0 ? HEAT_BULL : HEAT_BEAR;
  return scale[Math.min(Math.floor(Math.abs(pct) / 16), scale.length - 1)];
};

const SignalsHeatmap: React.FC<{ items: HeatmapItem[] }> = ({ items }) => (
  <Box display="flex" flexWrap="wrap" gap={0.8} p={1}>
    {[...items].sort((a, b) => Math.abs(b.composite_score) - Math.abs(a.composite_score)).map(item => {
      const pct = Math.round(item.composite_score * 100);
      const { bg, fg } = heatShade(pct);
      return (
        <Tooltip key={item.symbol} title={`${item.symbol}: ${pct > 0 ? '+' : ''}${pct} composite · ${item.count} signal(s)`}>
          <Box sx={{ bgcolor: bg, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, px: 1, py: 0.4, cursor: 'default', minWidth: 52, textAlign: 'center' }}>
            <Typography variant="caption" fontWeight={700} display="block" sx={{ color: fg }}>{item.symbol}</Typography>
            <Typography variant="caption" sx={{ color: fg, fontWeight: 800, fontSize: '0.7rem' }}>
              {pct > 0 ? '+' : ''}{pct}
            </Typography>
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

// ── Accuracy Table ────────────────────────────────────────────────────────

// Within 3 points of its base rate (or under 100 calls), a figure says nothing either way.
const vsBase = (r: AccuracyRow) => {
  const diff = (r.accuracy - r.base_rate) * 100;
  if (r.total < 100 || Math.abs(diff) < 3) return { diff, color: '#F9A825', note: 'about the base rate' };
  return diff > 0 ? { diff, color: BULL_BORDER, note: 'better than the base rate' }
    : { diff, color: BEAR_BORDER, note: 'worse than the base rate' };
};

const AccuracyTable: React.FC<{ rows: AccuracyRow[]; label: string }> = ({ rows, label }) => (
  <Box>
    <Typography variant="subtitle2" mb={1} color="text.secondary">{label}</Typography>
    {rows.length === 0 ? (
      <Typography variant="caption" color="text.secondary">No validated signals yet.</Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Calls</TableCell>
              <TableCell align="right">Right</TableCell>
              <TableCell align="right">
                <Tooltip title="How often the same days' stocks in the news moved the called way, whatever the model said">
                  <span>Base</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const v = vsBase(r);
              return (
                <TableRow key={i} hover sx={r.category === 'all' ? { '& td': { fontWeight: 700 } } : undefined}>
                  <TableCell><Chip size="small" label={r.category} variant={r.category === 'all' ? 'filled' : 'outlined'} /></TableCell>
                  <TableCell><Chip size="small" label={modelLabel(r.model)} color={modelColor(r.model)} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${r.bullish} bullish, ${r.bearish} bearish`}><span>{r.total}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${r.correct} right: ${v.diff >= 0 ? '+' : ''}${v.diff.toFixed(1)} points vs the base rate, ${v.note}`}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: v.color }}>
                        {Math.round(r.accuracy * 100)}%
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">{Math.round(r.base_rate * 100)}%</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

// ── Model Status Banner ───────────────────────────────────────────────────

const ModelStatusBanner: React.FC<{ status: SchedulerStatus | null }> = ({ status }) => {
  if (!status) return null;
  // Only show the banner if Ollama is down — Claude missing API key is expected
  if (status.qwen_available) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {status.claude_available ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <BlockIcon color="disabled" fontSize="small" />}
          <Typography variant="caption">
            🤖 Claude: {status.claude_available ? 'active' : 'no API key'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <BlockIcon color="warning" fontSize="small" />
          <Typography variant="caption">
            🦾 Qwen + 🧠 DeepSeek: Ollama not reachable — run <code>ollama serve</code>
          </Typography>
        </Stack>
      </Stack>
    </Alert>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────

const NewsSignals: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [hours, setHours] = useState(168);
  const [minArticles, setMinArticles] = useState(2);
  const [actionableOnly, setActionableOnly] = useState(true);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [notRelevant, setNotRelevant] = useState<NotRelevantArticle[]>([]);
  const [aggregated, setAggregated] = useState<StockAggregation[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapItem[]>([]);
  const [accuracy, setAccuracy] = useState<any>(null);
  const [status, setStatus] = useState<SchedulerStatus | null>(null);
  const [prices, setPrices] = useState<Record<string, PriceData>>({});
  const [stockMeta, setStockMeta] = useState<Record<string, StockMeta>>({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [feed, notRel, agg, heatmapData, accuracyData, statusData, pricesData, metaData] = await Promise.all([
        apiService.getNewsFeed(hours, 60),
        apiService.getNotRelevantFeed(hours, 100),
        apiService.getAggregatedSignals(hours, minArticles, actionableOnly),
        apiService.getSignalsHeatmap(hours),
        apiService.getSignalsAccuracy(),
        apiService.getSignalsStatus(),
        apiService.getAllPrices().catch(() => ({})),
        apiService.getStockMeta().catch(() => ({})),
      ]);
      setArticles(feed);
      setNotRelevant(notRel);
      setAggregated(agg);
      setHeatmap(heatmapData);
      setAccuracy(accuracyData);
      setStatus(statusData);
      setPrices(pricesData);
      setStockMeta(metaData);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [hours, minArticles, actionableOnly]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await apiService.refreshSignals();
      // Ingestion is instant; LLM analysis (Qwen + DeepSeek sequentially) takes ~2-4 minutes.
      // Reload after 10 s so the user sees new ingested articles right away,
      // then auto-reload again after 3 min when analysis is likely done.
      setTimeout(loadData, 10_000);
      setTimeout(loadData, 180_000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const pendingCount = status
    ? status.pending_articles
    : 0;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>News Signals</Typography>
          <Typography variant="caption" color="text.secondary">
            Hourly ingestion → LLM signal vectors per stock
            {lastRefresh && ` · Updated ${timeAgo(lastRefresh.toISOString())}`}
            {pendingCount > 0 && ` · ${pendingCount} pending analysis`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Window</InputLabel>
            <Select value={hours} label="Window" onChange={e => setHours(Number(e.target.value))}>
              <MenuItem value={6}>6h</MenuItem>
              <MenuItem value={24}>24h</MenuItem>
              <MenuItem value={48}>48h</MenuItem>
              <MenuItem value={72}>3 days</MenuItem>
              <MenuItem value={168}>7 days</MenuItem>
              <MenuItem value={720}>30 days</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small"
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleManualRefresh} disabled={refreshing}>
            Refresh
          </Button>
        </Stack>
      </Box>

      <ModelStatusBanner status={status} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats row */}
      <Grid container spacing={2} mb={2}>
        {[
          { label: 'Relevant Articles', value: articles.length, color: BULL_COLOR },
          { label: 'Not Relevant', value: notRelevant.length, color: 'text.disabled' },
          { label: 'Stocks Covered', value: heatmap.length, color: 'text.primary' },
          { label: 'Pending Analysis', value: status?.pending_articles ?? '—', color: (status?.pending_articles ?? 0) > 0 ? '#F57F17' : 'text.secondary' },
          { label: '🦾 Qwen Signals', value: status?.qwen_signals ?? '—', color: status?.qwen_available ? BULL_COLOR : BEAR_COLOR },
          { label: '🧠 DeepSeek Signals', value: status?.deepseek_signals ?? '—', color: status?.deepseek_available ? BULL_COLOR : BEAR_COLOR },
        ].map(stat => (
          <Grid item xs={6} sm={4} md={2} key={stat.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                <Typography variant="h5" fontWeight={700} color={stat.color}>
                  {loading ? '…' : stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Heatmap */}
      {heatmap.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
              Signal Heatmap — composite score per stock (green = bullish, red = bearish)
            </Typography>
            <SignalsHeatmap items={heatmap} />
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Relevant (${articles.length})`} />
        <Tab label={`Not Relevant (${notRelevant.length})`} />
        <Tab label={`Stock Aggregation (${aggregated.length})`} />
        <Tab label="Accuracy" />
      </Tabs>

      {/* Tab 0: Relevant articles */}
      {tab === 0 && (
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : articles.length === 0 ? (
            <Alert severity="info">
              No relevant articles yet. Click <b>Refresh</b> to trigger ingestion, or wait for the hourly scheduler.
            </Alert>
          ) : (
            articles.map(article => <ArticleCard key={article.id} article={article} stockMeta={stockMeta} />)
          )}
        </Box>
      )}

      {/* Tab 1: Not relevant articles */}
      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            These articles were analyzed but no NASDAQ stocks were found relevant to the content.
            {pendingCount > 0 && ` ${pendingCount} more articles are still in the analysis queue.`}
          </Alert>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : notRelevant.length === 0 ? (
            <Alert severity="success">All analyzed articles generated signals.</Alert>
          ) : (
            notRelevant.map(a => <NotRelevantCard key={a.id} article={a} />)
          )}
        </Box>
      )}

      {/* Tab 2: Stock Aggregation */}
      {tab === 2 && (
        <Box>
          {/* Filter bar */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Min Articles</InputLabel>
              <Select value={minArticles} label="Min Articles" onChange={e => setMinArticles(Number(e.target.value))}>
                <MenuItem value={1}>1+ articles</MenuItem>
                <MenuItem value={2}>2+ articles</MenuItem>
                <MenuItem value={3}>3+ articles</MenuItem>
                <MenuItem value={5}>5+ articles</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Post-mortem filter</InputLabel>
              <Select value={actionableOnly ? 'yes' : 'no'} label="Post-mortem filter"
                onChange={e => setActionableOnly(e.target.value === 'yes')}>
                <MenuItem value="yes">Forward-looking only</MenuItem>
                <MenuItem value="no">Include post-mortem</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {aggregated.length} stock{aggregated.length !== 1 ? 's' : ''} shown
            </Typography>
          </Stack>

          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            One row per stock, signals averaged across all articles. ⚠ = article describes a past price move (post-mortem).
            🦾 Qwen · 🧠 DeepSeek-R1 · Consensus = both models agree on direction.
          </Alert>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : aggregated.length === 0 ? (
            <Alert severity="info">
              No stocks match the current filters. Try lowering "Min Articles", widening the time window,
              or disabling "Forward-looking only".
            </Alert>
          ) : (
            aggregated.map(item => <AggregationCard key={item.symbol} item={item} price={prices[item.symbol]} />)
          )}
        </Box>
      )}

      {/* Tab 3: Historical Accuracy */}
      {tab === 3 && (
        <Box>
          {!accuracy ? <CircularProgress /> : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <AccuracyTable rows={accuracy.accuracy_1d} label="1-Day Accuracy" />
              </Grid>
              <Grid item xs={12} md={4}>
                <AccuracyTable rows={accuracy.accuracy_1w} label="1-Week Accuracy" />
              </Grid>
              <Grid item xs={12} md={4}>
                <AccuracyTable rows={accuracy.accuracy_1m} label="1-Month Accuracy" />
              </Grid>
            </Grid>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            A call is entered at the first close after it was made and is right when the price moved its way by
            0.5% or more, 1, 5 and 21 trading days later. Neutral calls are left out. <b>Base</b> is how often
            the stocks in the news on the same days moved that way, whatever the model said: a model is useful
            only when it beats its base. Green or red means more than 3 points from the base; amber means no
            better than the base.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default NewsSignals;
